import { useState, Suspense, ReactNode } from "react";
import { useFields } from "../../hooks/use-fields";
import { Box, BoxHeader, BoxTitle, BoxContent, BoxNavRight } from "../Box";
import { Segments, Segment } from "../SegmentedControl";
import { CAMPAIGN_TYPES } from "./constants";
import Input, { Label } from "../Input";
import { Plus, ChevronDown, TrashCan } from "../Icons";
import Stack from "../Stack";
import Composer from "../Composer";
import Button, { IconButton } from "../Button";
import Rules from "./Rules";
import { newMessageTemplateRule } from "./factories";
import { DatePickerOverlay } from "../DatePicker";
import moment from "moment-timezone";
import useMoment, { context as MomentContext } from "../../hooks/use-moment";
import MessageLinterRule, { lintMessage, TLintRule } from "../MessageLinter";
import MessageTemplatePreview from "./MessageTemplatePreview";
import { useSkipioQuery } from "../../hooks/use-query";
import Drawer from "../Drawer";
import { THEME_COLOR } from "../../vars";
import { CampaignType } from "./types";
import { OnChange } from "../../hooks/use-optional-state";
import { EditableMessageTemplate } from ".";

// The timezone of the deliverAt field is in utc
// so we need to create a moment constructor that makes
// utc dates. Unfortunately there's no way to clone a moment
// constructor and specify new options like there is in axios
const tzMoment = new Proxy(
  (...args: Parameters<typeof moment>) => {
    return moment(...args).utcOffset(0);
  },
  {
    get(target, prop: keyof typeof moment) {
      return moment[prop];
    },
  }
) as ((...args: Parameters<typeof moment>) => typeof moment) & typeof moment;

// Provide DatePicker with access to a UTC-based moment
function UTCMomentProvider({ children }: { children: ReactNode }) {
  return (
    <MomentContext.Provider value={{ moment: tzMoment }}>
      {children}
    </MomentContext.Provider>
  );
}

type MessageTemplateProps = {
  value: EditableMessageTemplate;
  onChange: OnChange<EditableMessageTemplate>;
  index: number;
  campaignType: CampaignType;
  onRemove: () => void;
};

function MessageTemplate({
  value,
  index,
  onChange,
  onRemove,
  campaignType,
}: MessageTemplateProps) {
  const moment = useMoment();
  const { errors = {} } = value;

  const { field, value: messageTemplate } = useFields({
    value,
    onChange,
  });

  const [isExpanded, setIsExpand] = useState(!messageTemplate.id);

  const selectedDeliveryStrategyTypes = Object.values(
    CAMPAIGN_TYPES[campaignType].deliveryTypes
  ).find((keys) => {
    const value = field("deliveryStrategy").value;
    return Object.keys(keys).some((key) => key === value);
  });

  const selectedLabelValue = Object.keys(selectedDeliveryStrategyTypes)[0];

  const messageKeywords =
    useSkipioQuery({
      queryKey: ["messageKeywords"],
      suspense: false,
      useErrorBoundary: true,
    }).data || [];

  let matchingLinterRules: TLintRule[] = [];
  if (index === 0 && !!value.body) {
    matchingLinterRules = lintMessage(value.body, {
      keywords: messageKeywords,
    });
  }

  return (
    <Box elevated error={Object.keys(errors).length > 0}>
      <BoxHeader
        style={{
          background: "#fff",
          boxShadow: "none",
        }}
      >
        <BoxTitle style={{ textAlign: "left" }}>
          {messageTemplate.name ? (
            <span data-ignore="true">{messageTemplate.name}</span>
          ) : (
            <>Text Message {index + 1}</>
          )}
        </BoxTitle>
        <BoxNavRight style={{ paddingRight: "8px" }}>
          <Stack>
            <IconButton onClick={onRemove}>
              <TrashCan />
            </IconButton>
            <IconButton
              onClick={
                isExpanded ? () => setIsExpand(false) : () => setIsExpand(true)
              }
            >
              <ChevronDown
                style={{ transform: `rotate(${isExpanded ? "0" : "-90"}deg)` }}
              />
            </IconButton>
          </Stack>
        </BoxNavRight>
      </BoxHeader>
      {!isExpanded ? (
        <BoxContent
          style={{ padding: "20px", paddingTop: 0, paddingBottom: "15px" }}
        >
          <MessageTemplatePreview messageTemplate={messageTemplate} />
        </BoxContent>
      ) : (
        <>
          <BoxContent>
            <Stack>
              <Segments
                small
                onChange={field("deliveryStrategy").onChange}
                value={selectedLabelValue}
              >
                {Object.entries(CAMPAIGN_TYPES[campaignType].deliveryTypes).map(
                  ([text, types], typeIndex) => (
                    <Segment key={typeIndex} value={Object.keys(types)[0]}>
                      {text}
                    </Segment>
                  )
                )}
              </Segments>
              <>
                <Box>
                  <BoxContent>
                    <Stack>
                      {Object.keys(selectedDeliveryStrategyTypes).length >
                        1 && (
                        <Segments small {...field("deliveryStrategy")}>
                          {Object.entries(selectedDeliveryStrategyTypes).map(
                            ([key, label]) => (
                              <Segment key={key} value={key}>
                                {label}
                              </Segment>
                            )
                          )}
                        </Segments>
                      )}
                      {messageTemplate.deliveryStrategy ===
                        "delay_in_minutes" && (
                        <Input
                          label="Minutes"
                          type="number"
                          min="1"
                          {...field("delayInMinutes")}
                          error={errors.delayInMinutes}
                        />
                      )}
                      {messageTemplate.deliveryStrategy === "delay_in_days" && (
                        <Stack horizontal>
                          <Input
                            label="Days"
                            type="number"
                            min="1"
                            {...field("delayInDays")}
                            error={errors.delayInDays}
                          />
                          <UTCMomentProvider>
                            <DatePickerOverlay
                              mode="time"
                              {...field("deliverAt")}
                              trigger={({ ref, show, isShowing }) => (
                                <Input
                                  onFocus={show}
                                  onChange={() => {}}
                                  focused={isShowing}
                                  ref={ref}
                                  label="Desired Delivery Time"
                                  readOnly
                                  error={errors.deliverAt}
                                  value={
                                    messageTemplate.deliverAt
                                      ? moment(messageTemplate.deliverAt)
                                          .utc()
                                          .format("LT")
                                      : ""
                                  }
                                />
                              )}
                            />
                          </UTCMomentProvider>
                        </Stack>
                      )}
                      {messageTemplate.deliveryStrategy ===
                        "days_before_event" && (
                        <Stack horizontal>
                          <Input
                            label="Days"
                            type="number"
                            min="1"
                            {...field("daysBeforeEvent")}
                            error={errors.daysBeforeEvent}
                          />
                          <UTCMomentProvider>
                            <DatePickerOverlay
                              mode="time"
                              {...field("deliverAt")}
                              trigger={({ ref, show, isShowing }) => (
                                <Input
                                  onFocus={show}
                                  onChange={() => {}}
                                  focused={isShowing}
                                  ref={ref}
                                  label="Desired Delivery Time"
                                  readOnly
                                  error={errors.deliverAt}
                                  value={
                                    messageTemplate.deliverAt
                                      ? moment(messageTemplate.deliverAt)
                                          .utc()
                                          .format("LT")
                                      : ""
                                  }
                                />
                              )}
                            />
                          </UTCMomentProvider>
                        </Stack>
                      )}

                      {messageTemplate.deliveryStrategy ===
                        "days_after_event" && (
                        <Stack horizontal>
                          <Input
                            label="Days"
                            type="number"
                            min="1"
                            {...field("daysAfterEvent")}
                            error={errors.daysAfterEvent}
                          />
                          <UTCMomentProvider>
                            <DatePickerOverlay
                              mode="time"
                              {...field("deliverAt")}
                              trigger={({ ref, show, isShowing }) => (
                                <Input
                                  onFocus={show}
                                  onChange={() => {}}
                                  focused={isShowing}
                                  ref={ref}
                                  label="Desired Delivery Time"
                                  readOnly
                                  value={
                                    messageTemplate.deliverAt
                                      ? moment(messageTemplate.deliverAt)
                                          .utc()
                                          .format("LT")
                                      : ""
                                  }
                                  error={errors.deliverAt}
                                />
                              )}
                            />
                          </UTCMomentProvider>
                        </Stack>
                      )}
                      {messageTemplate.deliveryStrategy ===
                        "minutes_before_event" && (
                        <Input
                          label="Minutes"
                          type="number"
                          min="1"
                          {...field("minutesBeforeEvent")}
                          error={errors.minutesBeforeEvent}
                        />
                      )}
                      {messageTemplate.deliveryStrategy ===
                        "minutes_after_event" && (
                        <Input
                          label="Minutes"
                          type="number"
                          min="1"
                          {...field("minutesAfterEvent")}
                          error={errors.minutesAfterEvent}
                        />
                      )}
                      <Input
                        label="Message Name for Reference"
                        {...field("name")}
                        error={errors.name}
                        required
                      />
                      <>
                        <Label>Message Body</Label>
                        <Suspense fallback={null}>
                          <Composer
                            value={value}
                            onChange={onChange}
                            enableScheduling={false}
                            error={errors.body?.[0]}
                            context={
                              campaignType !== "drip" ? "event" : "general"
                            }
                          />
                        </Suspense>
                      </>
                      {matchingLinterRules.length > 0 && (
                        <Suspense fallback={null}>
                          <Drawer
                            header={
                              <>
                                Message Mentor{" "}
                                <span style={{ color: THEME_COLOR }}>™</span>
                              </>
                            }
                          >
                            {matchingLinterRules.map((rule, index) => (
                              <MessageLinterRule rule={rule} key={index} />
                            ))}
                          </Drawer>
                        </Suspense>
                      )}
                    </Stack>
                  </BoxContent>
                </Box>
                {campaignType === "drip" && (
                  <Rules {...field("messageTemplateRules")} />
                )}
              </>
            </Stack>
          </BoxContent>
          <BoxContent style={{ padding: "4px" }}>
            <div style={{ textAlign: "center" }}>
              <Button
                variant="link"
                onClick={() =>
                  onChange((val: EditableMessageTemplate) => ({
                    ...val,
                    messageTemplateRules: [
                      ...val.messageTemplateRules,
                      newMessageTemplateRule(),
                    ],
                  }))
                }
              >
                <Plus /> <span>Add Rule</span>
              </Button>
            </div>
          </BoxContent>
        </>
      )}
    </Box>
  );
}

export default MessageTemplate;
